/**
 * Document Preview Panel Component
 * Shows markdown preview of a document in the right panel
 */

import { useState, useEffect } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MarkdownPreview } from '@/components/features/markdown-preview'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { fetchWithAuth } from '@/lib/api'
import { useInternalMarkdownLinkClick } from '@/lib/internal-link-navigation'

interface DocumentPreviewPanelProps {
  filePath: string
  title?: string
  onClose: () => void
  onEdit?: (filePath: string) => Promise<void>
}

export function DocumentPreviewPanel({
  filePath,
  title,
  onClose,
  onEdit,
}: DocumentPreviewPanelProps) {
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [documentTitle, setDocumentTitle] = useState<string>(title || '')
  const onInternalLinkClick = useInternalMarkdownLinkClick(filePath)

  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await fetchWithAuth(
          `/api/documents/by-path?path=${encodeURIComponent(filePath)}`,
        )
        if (!response.ok) {
          throw new Error('Failed to load document')
        }

        const doc = await response.json()
        setContent(doc.content || '')
        setDocumentTitle(
          doc.title || title || filePath.split('/').pop() || 'Document',
        )
      } catch (err) {
        console.error('[DocumentPreviewPanel] Error loading document:', err)
        setError(err instanceof Error ? err.message : 'Failed to load document')
      } finally {
        setLoading(false)
      }
    }

    loadDocument()
  }, [filePath, title])

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-card">
        <PreviewPaneHeader
          row
          icon={<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
          label="Document Preview"
          onClose={onClose}
        />
        <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          Loading preview…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col h-full bg-card">
        <PreviewPaneHeader
          row
          icon={<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
          label="Document Preview"
          onClose={onClose}
        />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      <PreviewPaneHeader
        row
        icon={<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
        label="Document Preview"
        chips={
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground font-semibold uppercase tracking-wider leading-none flex-shrink-0 truncate max-w-[160px]">
            {documentTitle}
          </span>
        }
        onOpen={
          onEdit
            ? () => {
                void onEdit(filePath)
              }
            : undefined
        }
        openTitle="Open in tab"
        onClose={onClose}
      />

      <div className="flex-1 min-h-0 overflow-y-auto">
        <MarkdownPreview
          content={content}
          className="max-w-none"
          readingColumnPadding="panel"
          onInternalLinkClick={onInternalLinkClick}
        />
      </div>
    </div>
  )
}
